import { useEffect, useCallback, useState, useMemo, FC } from 'react';
import { useSelector } from 'react-redux';
import { selectDirection } from 'store/globalSlice';
import { isRtl } from 'common/utils/utils';
import { usePlaceholderValues } from 'common/utils/hooks/usePlaceholderValues';
import TextArea from 'common/components/textArea';
import {
  selectForceFloatingTicketShowValidationError,
  selectForceShowValidationError,
} from 'store/fieldValidationsSlice';
import { StyledTextAreaWrapper } from './style';
import FieldName from '../fieldName';

interface TextFieldProps {
  displayName: string;
  field: {
    fieldName: string;
    fieldId: string;
    hint?: boolean;
    hintText?: string;
    templateFieldAttributes: {
      ticketTemplateFieldType: {
        id: string;
      };
    };
  };
  fieldValue: string | null;
  hasMoreDetails?: boolean;
  url?: string;
  urlParams?: any;
  isTemplatePage?: boolean;
  disabled?: boolean;
  required?: boolean;
  handleDirty?: (obj: Record<string, any>) => void;
  onFocus?: () => void;
  handleSaveValue: (obj: Record<string, any>) => void;
  updateFieldValidation?: (fieldName: string, required: boolean, isValid: boolean) => void;
  isNoEditPermission?: boolean;
  showNoPermissionPopup: () => void;
  isNewField?: boolean;
}

const TextField: FC<TextFieldProps> = ({
  displayName,
  field,
  fieldValue,
  hasMoreDetails,
  url,
  urlParams,
  isTemplatePage,
  disabled,
  required = false,
  handleDirty,
  onFocus,
  handleSaveValue,
  updateFieldValidation,
  isNoEditPermission,
  showNoPermissionPopup,
  isNewField,
}) => {
  const [textAreaValue, setTextAreaValue] = useState<string>(fieldValue ?? '');

  const forceShowError = useSelector(
    isNewField ? selectForceFloatingTicketShowValidationError : selectForceShowValidationError,
  );

  useEffect(() => {
    if (fieldValue !== null) {
      setTextAreaValue(fieldValue);
    }
  }, [fieldValue]);

  const direction = useSelector(selectDirection);
  const isRtlDirection = useMemo(() => isRtl(direction), [direction]);
  const placeholderValues = usePlaceholderValues();

  const fieldName = field.fieldName;

  const checkAndUpdateValidationStatus = useCallback(
    (value: string | null) => {
      const isValid = !(!value && required);

      if (updateFieldValidation) {
        updateFieldValidation(fieldName, required, isValid);
      }
      return isValid;
    },
    [required, fieldName, updateFieldValidation],
  );

  useEffect(() => {
    checkAndUpdateValidationStatus(fieldValue);
  }, [fieldValue, checkAndUpdateValidationStatus]);

  const handleValueChange = (value: string) => {
    setTextAreaValue(value);
  };

  const handleBlur = useCallback(() => {
    if (!fieldName) return;

    const currentValue = (textAreaValue ?? '').trim();
    const prevValue = (fieldValue ?? '').trim();

    if (currentValue === prevValue) return;

    const updateObj = { [fieldName]: textAreaValue };
    handleDirty?.(updateObj);
    handleSaveValue(updateObj);
  }, [fieldName, handleDirty, handleSaveValue, textAreaValue, fieldValue]);

  const placeholderValue = useMemo(() => {
    if (isNoEditPermission && !fieldValue) {
      return placeholderValues.NoEditingPermissions;
    }

    return placeholderValues.TypeHere;
  }, [fieldValue, isNoEditPermission, placeholderValues]);

  const isDisabled = useMemo(() => disabled || isNoEditPermission, [disabled, isNoEditPermission]);

  const checkIsError = useMemo(() => {
    const isInvalid = !textAreaValue && required;
    const newFieldError = isNewField && isInvalid && forceShowError;
    const existingFieldError = !isNewField && isInvalid;
    return newFieldError ?? existingFieldError;
  }, [textAreaValue, required, isNewField, forceShowError]);

  return (
    <>
      <FieldName
        url={url}
        name={displayName}
        required={required}
        urlParams={urlParams}
        fieldId={field?.fieldId}
        hasMoreDetails={hasMoreDetails}
        isTemplatePage={isTemplatePage}
        hintText={field?.hint && field?.hintText}
      />
      <StyledTextAreaWrapper isRtl={isRtlDirection}>
        <TextArea
          value={textAreaValue}
          setValue={handleValueChange}
          onBlur={handleBlur}
          onFocus={onFocus}
          disabled={isDisabled}
          placeholder={placeholderValue}
          isError={checkIsError}
          {...(isNoEditPermission && { onClick: showNoPermissionPopup })}
        />
      </StyledTextAreaWrapper>
    </>
  );
};

export default TextField;
